import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Drawer,
  Button,
  Icon,
  Tag,
  Badge,
  Tabs,
  Typography,
  Descriptions,
  Divider,
  Upload,
  message,
  Input,
  Modal,
  Select,
  Popover
} from 'antd'
import moment from 'moment'
import axios from 'util/Api'
import {
  getAlertTypeColor,
  formatAlertTypeLabel,
  getDueStatus,
  severityTag,
  formatDuration,
  getCurrentUser,
  getCurrentUserId,
  getOriginalAssigneeUserId,
  getOriginalAssigneeName,
  getOrganizationName,
  stripHtml,
  extractBodyContent
} from './utils'
import { buildAlertReportPdf, resolveSavedAnalysis } from './alertReportPdf'
import './styles.css'

const { Title, Text } = Typography
const { TextArea } = Input
const { TabPane } = Tabs
const { Option } = Select

const getAlertKey = (alert) => String((alert && (alert._id || alert.id)) || '')

// Human-friendly labels for the analysis content fields tracked in change history.
const CONTENT_FIELD_LABELS = {
  analysis: 'Analysis',
  actionSuggested: 'Action Suggested',
  actionTaken: 'Action Taken'
}

// Attribute each analysis content change (from the populated analysisId.changeHistory)
// to the status-history step during which it was made. The backend stamps each change
// with `changedAt` a moment after the step's own timestamp, so a change belongs to the
// latest step whose timestamp is <= changedAt. Returns { [statusHistoryIndex]: [changes] }.
const buildStatusChangesByIndex = (statusHistory) => {
  const result = {}
  if (!Array.isArray(statusHistory) || statusHistory.length === 0) return result

  const stepTime = (entry) => {
    const d = entry && (entry.timestamp || entry.date)
    return d ? moment(d).valueOf() : 0
  }

  // Collect unique changes across every referenced analysis doc (the same doc is
  // referenced by multiple steps, so its changeHistory would otherwise repeat).
  const seen = new Set()
  const changes = []
  statusHistory.forEach((entry) => {
    const analysis = entry && entry.analysisId
    const list = analysis && typeof analysis === 'object' && Array.isArray(analysis.changeHistory)
      ? analysis.changeHistory
      : []
    list.forEach((ch) => {
      if (!ch || !ch.field) return
      const at = ch.changedAt ? moment(ch.changedAt).valueOf() : null
      const key = `${ch.field}|${at}|${ch.oldValue}|${ch.newValue}`
      if (seen.has(key)) return
      seen.add(key)
      changes.push({ field: ch.field, oldValue: ch.oldValue, newValue: ch.newValue, at })
    })
  })
  if (changes.length === 0) return result

  // Steps ordered by time, keeping their original render index.
  const orderedSteps = statusHistory
    .map((entry, idx) => ({ idx, at: stepTime(entry) }))
    .sort((a, b) => a.at - b.at)

  changes.forEach((ch) => {
    if (ch.at === null) return
    let owner = orderedSteps[0]
    for (const step of orderedSteps) {
      if (step.at <= ch.at) owner = step
      else break
    }
    if (!result[owner.idx]) result[owner.idx] = []
    result[owner.idx].push(ch)
  })
  return result
}

function AlertDrawer({
  alert,
  relatedAlerts = [],
  activeAlertId,
  visible,
  expanded,
  onClose,
  onTabChange,
  onToggleExpand,
  onStatusUpdate,
  onAnalysisUpdate,
  onAssign,
  isDark = false
}) {
  const [analysisText, setAnalysisText] = useState('')
  const [actionSuggestedText, setActionSuggestedText] = useState('')
  const [actionTakenText, setActionTakenText] = useState('')
  const [ignoreComment, setIgnoreComment] = useState('')
  const [assignUserId, setAssignUserId] = useState(undefined)
  const [assignRemark, setAssignRemark] = useState('')
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignModalVisible, setAssignModalVisible] = useState(false)
  const [assignableUsers, setAssignableUsers] = useState([])
  const [assignableLoading, setAssignableLoading] = useState(false)
  const [fileList, setFileList] = useState([])
  const [lightboxVisible, setLightboxVisible] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [currentAlertId, setCurrentAlertId] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const surface = isDark ? '#2f3942' : '#ffffff'
  const surfaceAlt = isDark ? '#3b4550' : '#fafafa'
  const border = isDark ? '#44515b' : '#e8e8e8'
  const muted = isDark ? '#a1a1a1' : '#8c8c8c'
  const bodyText = isDark ? '#cbd5e1' : '#595959'
  const strongText = isDark ? '#e5e7eb' : '#262626'
  const sectionTitle = isDark ? '#d8dee6' : '#172b4d'
  const panelBg = isDark ? '#2b333b' : '#f0f2f5'
  const accent = isDark ? '#7fb3ff' : '#1890ff'

  // Content changes (analysis / action suggested / action taken) attributed to each
  // status-history step, surfaced via the info button in the Status Change History.
  const changesByIndex = useMemo(
    () => buildStatusChangesByIndex(alert && alert.statusHistory),
    [alert && alert.statusHistory]
  )

  // "Complete Action" / "Complete Action Directly" are restricted to the alert's
  // original assignee, OR that original assignee's supervisor when the supervisor's
  // role grants "Allow Closure of Supervisee Assigned Alerts". Other assignees/
  // observers can act on the alert but cannot complete it. The backend enforces this
  // and returns `canComplete` per alert — use that as the source of truth (it knows
  // the supervisor/role graph), falling back to a local original-assignee-only check
  // only when the flag is absent (older payloads).
  const isOriginalAssignee = typeof (alert && alert.canComplete) === 'boolean'
    ? alert.canComplete
    : (() => {
      const originalAssigneeId = getOriginalAssigneeUserId(alert)
      const currentUserId = getCurrentUserId()
      return Boolean(originalAssigneeId && currentUserId && String(originalAssigneeId) === String(currentUserId))
    })()
  const originalAssigneeName = getOriginalAssigneeName(alert)
  // Remark recorded when the CURRENT (most recently assigned) assignee was assigned.
  const currentAssigneeRemark = (() => {
    const recs = Array.isArray(alert && alert.assigneeRecords) ? alert.assigneeRecords : []
    const withTime = recs.filter((r) => r && typeof r.assignTime === 'number')
    if (!withTime.length) return ''
    const current = withTime.reduce((a, b) => (b.assignTime > a.assignTime ? b : a), withTime[0])
    return current && current.remarks ? String(current.remarks).trim() : ''
  })()
  const completeActionDisabledReason = isOriginalAssignee
    ? undefined
    : (originalAssigneeName
      ? `Only the original assignee (${originalAssigneeName}) or their supervisor can complete this alert`
      : 'Only the original assignee or their supervisor can complete this alert')

  // Popover body listing each field's old -> new value for one status step.
  const renderContentChanges = (changes) => (
    <div style={{ maxWidth: 380, maxHeight: 320, overflowY: 'auto', fontSize: 12 }}>
      {changes.map((ch, i) => {
        const oldText = (ch.oldValue === null || ch.oldValue === undefined || String(ch.oldValue).trim() === '')
          ? null
          : String(ch.oldValue)
        const newText = (ch.newValue === null || ch.newValue === undefined || String(ch.newValue).trim() === '')
          ? null
          : String(ch.newValue)
        return (
          <div key={i} style={{ marginBottom: i < changes.length - 1 ? 10 : 0 }}>
            <div style={{ fontWeight: 600, color: strongText, marginBottom: 4 }}>
              {CONTENT_FIELD_LABELS[ch.field] || ch.field}
            </div>
            {oldText && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: muted }}>From: </span>
                <span style={{ color: bodyText, whiteSpace: 'pre-wrap', textDecoration: 'line-through', opacity: 0.75 }}>{oldText}</span>
              </div>
            )}
            <div>
              <span style={{ color: muted }}>{oldText ? 'To: ' : 'Added: '}</span>
              <span style={{ color: bodyText, whiteSpace: 'pre-wrap' }}>{newText || '(empty)'}</span>
            </div>
          </div>
        )
      })}
    </div>
  )

  useEffect(() => {
    if (alert) {
      // Get alert ID to detect if it's a different alert
      const alertId = alert.id || alert._id
      const isDifferentAlert = alertId !== currentAlertId

      if (isDifferentAlert) {
        setCurrentAlertId(alertId)

        // Extract analysis data from alert object and statusHistory
        // This should run for both new alerts and updated alerts to sync with server data
        let analysisData = {
          analysis: alert.analysis || '',
          actionSuggested: alert.actionSuggested || '',
          actionTaken: alert.actionTaken || ''
        }

        if (alert.statusHistory && Array.isArray(alert.statusHistory)) {
          // Look through ALL statusHistory entries to find the most recent analysisId
          // Sort by timestamp (most recent first)
          const sortedHistory = [...alert.statusHistory].sort((a, b) => {
            const timeA = a.timestamp ? (moment.isMoment(a.timestamp) ? a.timestamp : moment(a.timestamp)) : moment(0)
            const timeB = b.timestamp ? (moment.isMoment(b.timestamp) ? b.timestamp : moment(b.timestamp)) : moment(0)
            return timeB.valueOf() - timeA.valueOf()
          })

          // Find the most recent statusHistory entry with analysisId
          for (const historyEntry of sortedHistory) {
            if (historyEntry.analysisId) {
              const analysisId = historyEntry.analysisId
              // analysisId can be an object with _id or the full object
              const analysisIdObj = typeof analysisId === 'object' && analysisId._id ? analysisId : analysisId

              if (analysisIdObj && typeof analysisIdObj === 'object') {
                // Extract data from analysisId object
                analysisData = {
                  analysis: analysisIdObj.analysis !== undefined && analysisIdObj.analysis !== null && analysisIdObj.analysis !== ''
                    ? analysisIdObj.analysis
                    : (alert.analysis || ''),
                  actionSuggested: analysisIdObj.actionSuggested !== undefined && analysisIdObj.actionSuggested !== null && analysisIdObj.actionSuggested !== ''
                    ? analysisIdObj.actionSuggested
                    : (alert.actionSuggested || ''),
                  actionTaken: analysisIdObj.actionTaken !== undefined && analysisIdObj.actionTaken !== null && analysisIdObj.actionTaken !== ''
                    ? analysisIdObj.actionTaken
                    : (alert.actionTaken || '')
                }
              }
              break // Use the most recent one found
            }
          }

          // Note: the actionTaken TEXT lives on analysisId (handled above). The
          // ACTION_TAKEN status-history entry's `remarks` holds the action label
          // ("Update action taken"), not the text, so it must not override it.
        }

        // New alert - always set the values
        setAnalysisText(analysisData.analysis)
        setActionSuggestedText(analysisData.actionSuggested)
        setActionTakenText(analysisData.actionTaken)
        setIgnoreComment('')
        setAssignUserId(undefined)
        setAssignRemark('')
        setAssignModalVisible(false)
      }
      // For same alert, don't update state - this prevents overwriting user input
      // The alert prop may change due to status updates, but we preserve user edits

      // Load documents from alert.documents array
      if (alert.documents && Array.isArray(alert.documents) && alert.documents.length > 0) {
        const mappedFiles = alert.documents.map((doc, index) => {
          const isImage = doc.file && doc.file.mimeType && doc.file.mimeType.startsWith('image/')
          return {
            uid: doc._id || `doc-${index}`,
            name: doc.file && doc.file.name ? doc.file.name : 'Unknown',
            status: 'done',
            url: doc.file && doc.file.path ? doc.file.path : '',
            thumbUrl: isImage ? (doc.file && doc.file.path ? doc.file.path : undefined) : undefined,
            response: doc,
            isImage: isImage,
            mimeType: doc.file && doc.file.mimeType ? doc.file.mimeType : ''
          }
        })
        setFileList(mappedFiles)
      } else if (alert.attachments && Array.isArray(alert.attachments) && alert.attachments.length > 0) {
        setFileList(alert.attachments)
      } else {
        setFileList([])
      }
    }
  }, [alert, currentAlertId])

  // Load assignable users (org users with access to the asset location/client)
  // when the Assign modal opens.
  useEffect(() => {
    let cancelled = false
    const alertId = alert && (alert._id || alert.id)
    if (assignModalVisible && alertId) {
      setAssignableLoading(true)
      axios.get(`/alerts/${alertId}/assignable-users`)
        .then((response) => {
          if (cancelled) return
          const raw = (response && response.data && (response.data.data || response.data)) || []
          const list = (Array.isArray(raw) ? raw : []).map((u) => ({
            id: String(u._id || u.id),
            name: u.name || u.email || String(u._id || u.id)
          }))
          setAssignableUsers(list)
        })
        .catch(() => {
          if (!cancelled) {
            setAssignableUsers([])
            message.error('Failed to load assignable users')
          }
        })
        .finally(() => {
          if (!cancelled) setAssignableLoading(false)
        })
    }
    return () => { cancelled = true }
  }, [assignModalVisible, alert])

  const handleAnalysisChange = (value) => {
    setAnalysisText(value)
    // Don't call onAnalysisUpdate here - it causes the alert prop to update
    // which triggers useEffect and resets the state, making fields non-editable
    // Values will be sent when status is updated via onStatusUpdate
  }

  const handleActionSuggestedChange = (value) => {
    setActionSuggestedText(value)
    // Don't call onAnalysisUpdate here - it causes the alert prop to update
    // which triggers useEffect and resets the state, making fields non-editable
    // Values will be sent when status is updated via onStatusUpdate
  }

  const handleActionTakenChange = (value) => {
    setActionTakenText(value)
    // Don't call onAnalysisUpdate here - it causes the alert prop to update
    // which triggers useEffect and resets the state, making fields non-editable
    // Values will be sent when status is updated via onStatusUpdate
  }

  const handleFileChange = (info) => {
    const { file, fileList: newFileList } = info

    // Update file list
    setFileList(newFileList.slice(-10))

    // Handle upload status
    if (file.status === 'done') {
      message.success(`${file.name} uploaded successfully`)
      // File is already uploaded via beforeUpload, so we can update the alert
      if (alert && onAnalysisUpdate) {
        onAnalysisUpdate(alert.id, { attachments: newFileList.slice(-10) })
      }
    } else if (file.status === 'error') {
      message.error(`${file.name} upload failed`)
    }
  }

  const handleFileRemove = (file) => {
    // Get document _id from file response or uid
    const docId = file.response && file.response._id ? file.response._id : file.uid

    if (!alert || !alert._id || !docId) {
      message.error('Cannot delete: Missing alert or document ID')
      return false // Prevent default removal
    }

    // Show confirmation dialog
    Modal.confirm({
      title: 'Delete Document',
      content: `Are you sure you want to delete "${file.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // Call DELETE API
          const response = await axios.delete(`/alerts/${alert._id}/document/${docId}`)

          // Check if API returned updated alert object with documents array
          if (response && response.data) {
            const responseData = response.data.data || response.data

            // If API returns full alert object with documents array, use that
            if (responseData.documents && Array.isArray(responseData.documents)) {
              const mappedFiles = responseData.documents.map((doc, index) => {
                const isImage = doc.file && doc.file.mimeType && doc.file.mimeType.startsWith('image/')
                return {
                  uid: doc._id || `doc-${index}`,
                  name: doc.file && doc.file.name ? doc.file.name : 'Unknown',
                  status: 'done',
                  url: doc.file && doc.file.path ? doc.file.path : '',
                  thumbUrl: isImage ? (doc.file && doc.file.path ? doc.file.path : undefined) : undefined,
                  response: doc,
                  isImage: isImage,
                  mimeType: doc.file && doc.file.mimeType ? doc.file.mimeType : ''
                }
              })
              setFileList(mappedFiles)

              // Update alert object with new documents - this will trigger useEffect to refresh
              if (onAnalysisUpdate) {
                onAnalysisUpdate(alert.id || alert._id, { documents: responseData.documents })
              }

              message.success('Document deleted successfully')
            } else {
              // Fallback: if API doesn't return documents array, remove from local state
              const newFileList = fileList.filter(item => item.uid !== file.uid)
              setFileList(newFileList)

              if (onAnalysisUpdate) {
                onAnalysisUpdate(alert.id || alert._id, { attachments: newFileList })
              }

              message.success('Document deleted successfully')
            }
          } else {
            // If no response data, fallback to local removal
            const newFileList = fileList.filter(item => item.uid !== file.uid)
            setFileList(newFileList)

            if (onAnalysisUpdate) {
              onAnalysisUpdate(alert.id || alert._id, { attachments: newFileList })
            }

            message.success('Document deleted successfully')
          }
        } catch (error) {
          console.error('Error deleting document:', error)
          message.error('Failed to delete document. Please try again.')
        }
      }
    })

    // Return false to prevent default removal behavior
    // The file will only be removed after user confirms in the dialog
    return false
  }

  const handlePreview = (file) => {
    const index = fileList.findIndex(item => item.uid === file.uid)
    setLightboxIndex(index >= 0 ? index : 0)
    setLightboxVisible(true)
  }

  const getFileIcon = (mimeType) => {
    if (!mimeType) return 'file'
    if (mimeType.includes('pdf')) return 'file-pdf'
    if (mimeType.includes('word') || mimeType.includes('document')) return 'file-word'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'file-excel'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'file-ppt'
    if (mimeType.includes('text')) return 'file-text'
    return 'file'
  }

  const beforeUpload = (file) => {
    // Validation only
    const isImage = file.type.startsWith('image/')
    const isDocument = file.type.includes('pdf') ||
      file.type.includes('document') ||
      file.type.includes('text') ||
      file.type.includes('spreadsheet') ||
      file.type.includes('presentation') ||
      file.name.endsWith('.doc') ||
      file.name.endsWith('.docx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.ppt') ||
      file.name.endsWith('.pptx') ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.csv')

    if (!isImage && !isDocument) {
      message.error('You can only upload images or documents!')
      return false
    }

    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error('File must be smaller than 10MB!')
      return false
    }

    if (!alert || !alert._id) {
      message.error('Alert ID not found')
      return false
    }

    return true // Allow upload to proceed
  }

  const customRequest = async ({ file, onSuccess, onError, onProgress }) => {
    // Create FormData and append the file
    const formData = new FormData()
    formData.append('file', file)

    try {
      // Upload file to API
      const response = await axios.post(`/alerts/${alert._id}/document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            if (onProgress) {
              onProgress({ percent })
            }
          }
        }
      })

      // Check if API returned updated alert object with documents
      if (response && response.data) {
        const responseData = response.data.data || response.data

        // If API returns full alert object with documents array, use that
        if (responseData.documents && Array.isArray(responseData.documents)) {
          const mappedFiles = responseData.documents.map((doc, index) => {
            const isImage = doc.file && doc.file.mimeType && doc.file.mimeType.startsWith('image/')
            return {
              uid: doc._id || `doc-${index}`,
              name: doc.file && doc.file.name ? doc.file.name : 'Unknown',
              status: 'done',
              url: doc.file && doc.file.path ? doc.file.path : '',
              thumbUrl: isImage ? (doc.file && doc.file.path ? doc.file.path : undefined) : undefined,
              response: doc,
              isImage: isImage,
              mimeType: doc.file && doc.file.mimeType ? doc.file.mimeType : ''
            }
          })
          setFileList(mappedFiles)

          // Update alert object with new documents - this will trigger useEffect to refresh
          if (onAnalysisUpdate) {
            onAnalysisUpdate(alert.id || alert._id, { documents: responseData.documents })
          }

          // Call onSuccess with updated file
          if (onSuccess) {
            const updatedFile = {
              ...file,
              status: 'done',
              url: mappedFiles[mappedFiles.length - 1] && mappedFiles[mappedFiles.length - 1].url ? mappedFiles[mappedFiles.length - 1].url : '',
              thumbUrl: mappedFiles[mappedFiles.length - 1] && mappedFiles[mappedFiles.length - 1].thumbUrl ? mappedFiles[mappedFiles.length - 1].thumbUrl : '',
              response: responseData
            }
            onSuccess(response.data, updatedFile)
          }
        } else {
          // Fallback: if API returns only the document object, add it to existing list
          const isImage = file.type && file.type.startsWith('image/')
          const filePath = responseData.file && responseData.file.path ? responseData.file.path : (responseData.url || responseData.fileUrl)
          const uploadedFile = {
            uid: responseData._id || file.uid,
            name: file.name,
            status: 'done',
            url: filePath,
            thumbUrl: isImage ? filePath : undefined,
            response: responseData,
            isImage: isImage,
            mimeType: responseData.file && responseData.file.mimeType ? responseData.file.mimeType : file.type || ''
          }

          const newFileList = [...fileList, uploadedFile].slice(-10)
          setFileList(newFileList)

          if (onAnalysisUpdate) {
            onAnalysisUpdate(alert.id || alert._id, { attachments: newFileList })
          }

          // Call onSuccess with updated file
          if (onSuccess) {
            onSuccess(response.data, uploadedFile)
          }
        }
      } else {
        // If no response data, still call onSuccess to mark upload as complete
        if (onSuccess) {
          onSuccess({}, file)
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      message.error(`Failed to upload ${file.name}. Please try again.`)
      if (onError) {
        onError(error)
      }
    }
  }

  const generateWhatsAppMessage = () => {
    if (!alert) return ''

    const orgName = getOrganizationName()
    const alertDate = moment(alert.createdAt).format('DD MMM YYYY')
    const alertTime = moment(alert.createdAt).format('HH:mm')
    const alertDateTime = `${alertDate} ${alertTime}`

    // Get asset model - combine asset type, sub type, and model if available
    const modelParts = []
    if (alert.assetType) modelParts.push(alert.assetType)
    if (alert.assetSubType) modelParts.push(alert.assetSubType)
    if (alert.assetModel) modelParts.push(alert.assetModel)
    const model = modelParts.length > 0 ? modelParts.join(' ') : 'N/A'

    const cleanForWhatsApp = (value) => {
      const cleaned = stripHtml(String(value || ''))
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trim()
      return cleaned || 'N/A'
    }

    // Strip HTML from alert body
    const alertTitle = cleanForWhatsApp(alert.title || 'N/A')

    // Get analysis and action suggested from state or alert data
    const analysis = cleanForWhatsApp(analysisText || alert.analysis || 'N/A')
    const actionSuggested = cleanForWhatsApp(actionSuggestedText || alert.actionSuggested || 'N/A')

    const message = `*AIM Update:*
Serial No: ${alert.id || 'N/A'}
Date: ${alertDateTime}
Organisation Name: ${orgName}
Asset Code: ${alert.assetCode || 'N/A'}${alert.altName ? ` ${alert.altName}` : ''}${alert.ownerCode ? ` ${alert.ownerCode}` : ''}
Model: ${model}
*Alert:* ${alertTitle}
*Analysis:*
${analysis}
*Suggested Action:*
${actionSuggested}`

    return message
  }

  const copyWhatsAppMessage = () => {
    const whatsappMessage = generateWhatsAppMessage()
    const textarea = document.createElement('textarea')
    textarea.value = whatsappMessage
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand('copy')
      message.success('WhatsApp message copied to clipboard!')
    } catch (err) {
      message.error('Failed to copy message. Please try again.')
      console.error('Failed to copy:', err)
    }

    document.body.removeChild(textarea)
  }

  // Client-shareable PDF of the SAVED alert (see alertReportPdf.js). Unsaved text in
  // the Analysis / Action boxes is deliberately not used, so the user is told.
  const downloadPdfReport = async () => {
    if (!alert || pdfLoading) return
    const saved = resolveSavedAnalysis(alert)
    const hasUnsavedEdits = (analysisText || '').trim() !== saved.analysis ||
      (actionSuggestedText || '').trim() !== saved.actionSuggested ||
      (actionTakenText || '').trim() !== saved.actionTaken
    if (hasUnsavedEdits) {
      message.warning('You have unsaved changes. The PDF uses the last saved Analysis / Action — update them first to include your edits.', 6)
    }
    setPdfLoading(true)
    try {
      const organisation = getOrganizationName()
      const result = await buildAlertReportPdf({
        alert,
        organisation: organisation === 'N/A' ? '' : organisation,
        attachments: fileList.filter((f) => f && f.status === 'done' && f.url)
      })
      const problems = []
      if (result.failed.length > 0) problems.push(`${result.failed.length} image(s) could not be embedded`)
      if (result.unprintable > 0) problems.push(`${result.unprintable} character(s) could not be printed and show as [?]`)
      if (problems.length > 0) {
        message.warning(`PDF downloaded, but ${problems.join(' and ')}. See the notes at the end of the PDF.`, 8)
      } else {
        message.success('PDF report downloaded')
      }
    } catch (err) {
      console.error('Alert PDF generation failed:', err)
      message.error('Could not generate the PDF report. Please try again.')
    } finally {
      setPdfLoading(false)
    }
  }

  const alertsForTabs = Array.isArray(relatedAlerts) && relatedAlerts.length > 0
    ? relatedAlerts
    : (alert ? [alert] : [])
  const linkedAlertsCount = Math.max(alertsForTabs.length - 1, 0)
  const alertSequenceByType = {}
  const tabDefinitions = alertsForTabs.map((tabAlert, idx) => {
    const type = tabAlert.type || 'ALERT'
    alertSequenceByType[type] = (alertSequenceByType[type] || 0) + 1
    const sequence = alertSequenceByType[type]
    const key = getAlertKey(tabAlert) || String(idx)
    const typeLabel = formatAlertTypeLabel(type) || type.replace(/_/g, ' ')
    return {
      key,
      label: `${typeLabel} ${sequence}`,
      alertId: tabAlert.id || 'Alert'
    }
  })
  const activeTabKey = String(
    activeAlertId
    || getAlertKey(alert)
    || (tabDefinitions[0] && tabDefinitions[0].key)
    || ''
  )

  if (!alert) return null

  const severityInfo = severityTag(alert.severity)
  const typeColor = getAlertTypeColor(alert.type)
  const alertTypeLabel = formatAlertTypeLabel(alert.type) || alert.type || 'ALERT'
  const dueStatus = getDueStatus(alert)
  const daysSinceCreation = moment().diff(moment(alert.createdAt), 'days')
  const isNewAlert = alert.status === 'NEW' || alert.status === 'New'

  return (
    <Drawer
      className={`alert-drawer ${expanded ? 'alert-drawer-expanded' : ''}`}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>
                {alert.id || 'Alert Details'}
              </span>
              <Tag color={severityInfo.color}>{severityInfo.text}</Tag>
              {linkedAlertsCount > 0 && (
                <Tag
                  color="red"
                  style={{
                    fontWeight: 700,
                    borderRadius: 999,
                    fontSize: 11,
                    padding: '2px 8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginRight: 0
                  }}
                >
                  <Icon type="link" />
                  +{linkedAlertsCount} LINKED
                </Tag>
              )}
            </div>
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                background: typeColor.bg,
                color: typeColor.color,
                fontWeight: 600
              }}
            >
              {alertTypeLabel}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Button
              icon="file-pdf"
              loading={pdfLoading}
              onClick={downloadPdfReport}
              title="Download a PDF report of this alert to share with the client"
              style={{ marginRight: 4 }}
            >
              PDF
            </Button>
            <Button
              type="text"
              onClick={onToggleExpand}
              style={{
                width: 40,
                height: 40,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                border: 'none',
                boxShadow: 'none'
              }}
              title={expanded ? 'Collapse' : 'Expand to full width'}
            >
              <Icon type={expanded ? 'fullscreen-exit' : 'fullscreen'} />
            </Button>
            <Button
              type="text"
              icon="close"
              onClick={onClose}
              style={{
                width: 40,
                height: 40,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                border: 'none',
                boxShadow: 'none'
              }}
              title="Close"
            />
          </div>
        </div>
      }
      placement="right"
      closable={false}
      onClose={onClose}
      visible={visible}
      width={expanded ? '100%' : 400}
      bodyStyle={{
        padding: 24,
        borderLeft: expanded ? `1px solid ${border}` : 'none',
        background: surface,
        color: strongText
      }}
      style={expanded ? { maxWidth: '100vw' } : {}}
    >
      {tabDefinitions.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Tabs
            activeKey={activeTabKey}
            onChange={(key) => {
              if (onTabChange) onTabChange(key)
            }}
            size="small"
            animated={false}
          >
            {tabDefinitions.map((tab) => (
              <TabPane
                tab={tab.label}
                key={tab.key}
              />
            ))}
          </Tabs>
        </div>
      )}

      {/* Alert Details Summary */}
      <div style={{
        marginBottom: 20,
        padding: '12px',
        background: surfaceAlt,
        borderRadius: 6,
        fontSize: 11,
        border: `1px solid ${border}`
      }}>
        <Descriptions column={2} bordered={false} size="small" style={{ marginBottom: 0 }}>
          <Descriptions.Item label="Alert ID">{alert.id}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge
              status={
                (alert.status === 'NEW' || alert.status === 'New') ? 'processing' :
                  (alert.status === 'UNDER_ANALYSIS' || alert.status === 'Under Analysis') ? 'warning' :
                    (alert.status === 'ACTION_PENDING' || alert.status === 'Action Pending') ? 'error' : 'default'
              }
              text={alert.status}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Severity">
            <span
              style={{
                fontSize: 9,
                padding: '2px 6px',
                borderRadius: 999,
                background: severityInfo.color === 'red' ? '#ff5630' : severityInfo.color === 'orange' ? '#ffab00' : '#0065ff',
                color: '#fff',
                fontWeight: 'bold'
              }}
            >
              {alert.severity.toUpperCase()}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                background: typeColor.bg,
                color: typeColor.color,
                fontWeight: 600
              }}
            >
              {alertTypeLabel}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Asset Type / Sub Type / Model">
            {(() => {
              const parts = []
              if (alert.assetType) parts.push(alert.assetType)
              if (alert.assetSubType) parts.push(alert.assetSubType)
              if (alert.assetModel) parts.push(alert.assetModel)
              return parts.length > 0 ? parts.join(' / ') : ''
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="Asset Code / Owner Code / Alt Name">
            {(() => {
              const parts = []
              if (alert.manpowerName) parts.push(alert.manpowerName)
              if (alert.assetCode) parts.push(alert.assetCode)
              if (alert.ownerCode) parts.push(alert.ownerCode)
              if (alert.altName) parts.push(alert.altName)
              return parts.length > 0 ? parts.join(' / ') : ''
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="Location / Sub Location">
            {(() => {
              const parts = []
              if (alert.location) parts.push(alert.location)
              if (alert.subLocation) parts.push(alert.subLocation)
              return parts.length > 0 ? parts.join(' / ') : ''
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="Assignee">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <div>
                {alert.assignee === 'Unassigned' ? (
                  <Tag color="default">Unassigned</Tag>
                ) : (
                  alert.assignee
                )}
                {currentAssigneeRemark && (
                  <div style={{ fontSize: 11, color: muted, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                    <span style={{ fontWeight: 600 }}>NOTE:</span> {currentAssigneeRemark}
                  </div>
                )}
              </div>
              {onAssign && (
                <Button size="small" icon="user-add" onClick={() => setAssignModalVisible(true)}>
                  Assign
                </Button>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item span={alert.dueAt ? 1 : 2}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: '#e3fcef',
                  color: '#006644',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  display: 'inline-block',
                  width: 'fit-content'
                }}
              >
                {daysSinceCreation} {daysSinceCreation === 1 ? 'day' : 'days'}
              </span>
              <div style={{ fontSize: 11, color: muted, lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600, color: strongText }}>Created: </span>
                {moment(alert.createdAt).format('DD MMM YYYY, HH:mm:ss')}
              </div>
            </div>
          </Descriptions.Item>
          {alert.dueAt && (
            <Descriptions.Item>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span
                  style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 999,
                    background: (dueStatus && dueStatus.status === 'overdue') ? '#ffe5e5' : (dueStatus && dueStatus.status === 'due') ? '#fff7e6' : '#e9f2ff',
                    color: (dueStatus && dueStatus.status === 'overdue') ? '#cf1322' : (dueStatus && dueStatus.status === 'due') ? '#faad14' : '#0747a6',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    display: 'inline-block',
                    width: 'fit-content'
                  }}
                >
                  {(dueStatus && dueStatus.status === 'overdue') && (
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#ff5630', marginRight: 4, verticalAlign: 'middle' }} />
                  )}
                  {(dueStatus && dueStatus.status === 'overdue')
                    ? `Overdue ${dueStatus.days}d`
                    : (dueStatus && dueStatus.status === 'due')
                      ? 'Due today'
                      : dueStatus ? `Due in ${dueStatus.days}d` : ''
                  }
                </span>
                <div style={{ fontSize: 11, color: muted, lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 600, color: strongText }}>Action Due Date: </span>
                  {moment(alert.dueAt).format('DD MMM YYYY, HH:mm')}
                </div>
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      {/* Assign to modal */}
      {onAssign && (
        <Modal
          title="Assign to"
          visible={assignModalVisible}
          confirmLoading={assignLoading}
          okText="Assign"
          okButtonProps={{ disabled: !assignUserId, icon: 'user-add' }}
          onCancel={() => { if (!assignLoading) setAssignModalVisible(false) }}
          onOk={async () => {
            if (!assignUserId) {
              message.error('Please select a user to assign')
              return
            }
            setAssignLoading(true)
            try {
              const ok = await onAssign(alert, { user: assignUserId, remarks: assignRemark })
              if (ok) {
                setAssignUserId(undefined)
                setAssignRemark('')
                setAssignModalVisible(false)
              }
            } finally {
              setAssignLoading(false)
            }
          }}
        >
          <div style={{ marginBottom: 8, fontSize: 12, color: muted }}>
            Once an alert is assigned, you can reassign it to your supervisor, anyone in
            your reporting team (your reports and theirs), yourself, or the alert's
            original assignee.
          </div>
          <Select
            showSearch
            value={assignUserId}
            placeholder="Select a user"
            optionFilterProp="children"
            style={{ width: '100%', marginBottom: 8 }}
            loading={assignableLoading}
            notFoundContent={assignableLoading ? 'Loading...' : 'No eligible users to assign'}
            onChange={(val) => setAssignUserId(val)}
            filterOption={(input, option) => String(option.props.children || '').toLowerCase().includes(input.toLowerCase())}
          >
            {assignableUsers.map((u) => (
              <Option key={u.id} value={u.id}>{u.name || u.id}</Option>
            ))}
          </Select>
          <TextArea
            rows={3}
            value={assignRemark}
            onChange={(e) => setAssignRemark(e.target.value)}
            placeholder="Add a remark (optional)"
          />
        </Modal>
      )}

      {/* Title and Body */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 12, fontSize: 20, fontWeight: 600, color: strongText }}>
          {stripHtml(alert.title || '')}
        </Title>
        {alert.body && (
          <div
            style={{
              marginBottom: 0,
              color: bodyText,
              fontSize: 15,
              lineHeight: 1.7,
              wordWrap: 'break-word'
            }}
          >
            <style>{`
              .alert-body-content p {
                margin: 0 0 12px 0;
              }
              .alert-body-content p:last-child {
                margin-bottom: 0;
              }
              .alert-body-content strong, .alert-body-content b {
                font-weight: 600;
                color: ${strongText};
              }
              .alert-body-content ul, .alert-body-content ol {
                margin: 12px 0;
                padding-left: 24px;
              }
              .alert-body-content li {
                margin: 6px 0;
              }
            `}</style>
            <div
              className="alert-body-content"
              dangerouslySetInnerHTML={{ __html: extractBodyContent(alert.body || '') }}
            />
          </div>
        )}
      </div>

      {/* Workflow Actions (hidden only once the alert is closed/completed) */}
      {(alert.status !== 'CLOSED' && alert.status !== 'Closed') && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: sectionTitle,
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: `1px solid ${border}`
          }}>
            Workflow Actions
          </div>

          {/* Start Analysis Button - For NEW status (textboxes hidden by default) */}
          {isNewAlert && (
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                icon="edit"
                onClick={() => onStatusUpdate && onStatusUpdate(alert, 'UNDER_ANALYSIS', 'Start Analysis', {
                  analysis: analysisText,
                  actionSuggested: actionSuggestedText,
                  actionTaken: actionTakenText
                })}
                block
              >
                Start Analysis
              </Button>
            </div>
          )}

          {/* Analysis and Action Suggested Fields - Visible from UNDER_ANALYSIS onwards */}
          {(alert.status === 'UNDER_ANALYSIS' || alert.status === 'ACTION_PENDING' || alert.status === 'ACTION_TAKEN' ||
            alert.status === 'Under Analysis' || alert.status === 'Action Pending' || alert.status === 'Action Taken') && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Analysis</div>
                  <TextArea
                    rows={4}
                    value={analysisText}
                    onChange={(e) => handleAnalysisChange(e.target.value)}
                    placeholder="Enter your analysis here..."
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Suggested</div>
                  <TextArea
                    rows={4}
                    value={actionSuggestedText}
                    onChange={(e) => handleActionSuggestedChange(e.target.value)}
                    placeholder="Enter suggested action here..."
                  />
                </div>
              </>
            )}

          {/* Update Analysis Button - Available from UNDER_ANALYSIS onwards */}
          {(alert.status === 'UNDER_ANALYSIS' || alert.status === 'Under Analysis' ||
            alert.status === 'ACTION_PENDING' || alert.status === 'Action Pending' ||
            alert.status === 'ACTION_TAKEN' || alert.status === 'Action Taken') && (
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="default"
                  size="large"
                  icon="edit"
                  onClick={() => {
                    // Keep the same status when updating
                    const currentStatus = alert.status === 'Under Analysis' ? 'UNDER_ANALYSIS'
                      : alert.status === 'Action Pending' ? 'ACTION_PENDING'
                        : alert.status === 'Action Taken' ? 'ACTION_TAKEN'
                          : alert.status
                    onStatusUpdate && onStatusUpdate(alert, currentStatus, 'Update Analysis', {
                      analysis: analysisText,
                      actionSuggested: actionSuggestedText
                      // actionTaken excluded from Update Analysis
                    })
                  }}
                  disabled={!(analysisText || '').trim() && !(actionSuggestedText || '').trim()}
                  block
                >
                  Update Analysis
                </Button>
              </div>
            )}

          {/* Complete Analysis Button - Only for UNDER_ANALYSIS */}
          {(alert.status === 'UNDER_ANALYSIS' || alert.status === 'Under Analysis') && (
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                icon="check"
                onClick={() => onStatusUpdate && onStatusUpdate(alert, 'ACTION_PENDING', 'Complete Analysis', {
                  analysis: analysisText,
                  actionSuggested: actionSuggestedText,
                  actionTaken: actionTakenText
                })}
                block
              >
                Complete Analysis
              </Button>
            </div>
          )}

          {/* Action Taken Field - For ACTION_PENDING and ACTION_TAKEN */}
          {(alert.status === 'ACTION_PENDING' || alert.status === 'Action Pending' ||
            alert.status === 'ACTION_TAKEN' || alert.status === 'Action Taken') && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Taken</div>
                <TextArea
                  rows={4}
                  value={actionTakenText}
                  onChange={(e) => handleActionTakenChange(e.target.value)}
                  placeholder="Enter action taken here..."
                />
              </div>
              {/* Update Action Taken - records the action and moves the alert into
                  the "Action Taken" column (ACTION_TAKEN) without closing it. */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="default"
                  size="large"
                  icon="edit"
                  onClick={() => onStatusUpdate && onStatusUpdate(alert, 'ACTION_TAKEN', 'Update action taken', {
                    // analysis: analysisText,
                    // actionSuggested: actionSuggestedText,
                    actionTaken: actionTakenText
                  })}
                  disabled={!(actionTakenText || '').trim()}
                  block
                >
                  Update Action Taken
                </Button>
              </div>

              {/* Complete Action - closes the alert (CLOSED), removing it from the board.
                  Restricted to the alert's original assignee, and requires an
                  "Action Taken" note. */}
              <div style={{ marginBottom: 16 }} title={completeActionDisabledReason}>
                <Button
                  type="primary"
                  size="large"
                  icon="check-circle"
                  disabled={!isOriginalAssignee || !actionTakenText.trim()}
                  onClick={() => onStatusUpdate && onStatusUpdate(alert, 'CLOSED', 'Action Completed', {
                    analysis: analysisText,
                    actionSuggested: actionSuggestedText,
                    actionTaken: actionTakenText
                  })}
                  block
                >
                  Complete Action
                </Button>
                {!isOriginalAssignee ? (
                  <div style={{ color: muted, fontSize: 11, marginTop: 6, textAlign: 'center' }}>
                    <Icon type="lock" style={{ marginRight: 4 }} />
                    {completeActionDisabledReason}
                  </div>
                ) : !actionTakenText.trim() ? (
                  <div style={{ color: muted, fontSize: 11, marginTop: 6, textAlign: 'center' }}>
                    <Icon type="info-circle" style={{ marginRight: 4 }} />
                    Enter what action was taken before completing.
                  </div>
                ) : null}
              </div>

              {/* WhatsApp Message Button */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="default"
                  size="large"
                  icon="message"
                  onClick={copyWhatsAppMessage}
                  block
                  style={{
                    background: '#25D366',
                    borderColor: '#25D366',
                    color: '#fff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#20BA5A'
                    e.currentTarget.style.borderColor = '#20BA5A'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#25D366'
                    e.currentTarget.style.borderColor = '#25D366'
                  }}
                >
                  Copy WhatsApp Message
                </Button>
              </div>
            </>
          )}

          {/* Direct Action Section - Only visible for NEW status (before analysis) */}
          {isNewAlert && (
            <>
              <Divider>Or</Divider>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Taken (Direct - Skip Analysis)</div>
                <TextArea
                  rows={3}
                  value={actionTakenText}
                  onChange={(e) => setActionTakenText(e.target.value)}
                  placeholder="Enter action taken directly (without analysis)..."
                />
                <Button
                  type="default"
                  size="large"
                  icon="check-circle"
                  disabled={!isOriginalAssignee || !actionTakenText.trim()}
                  title={completeActionDisabledReason}
                  onClick={() => onStatusUpdate && onStatusUpdate(alert, 'DIRECT_ACTION', 'Direct Action', {
                    analysis: analysisText,
                    actionSuggested: actionSuggestedText,
                    actionTaken: actionTakenText,
                    directAction: true
                  })}
                  block
                  style={{ marginTop: 8 }}
                >
                  Complete Action Directly
                </Button>
                {!isOriginalAssignee ? (
                  <div style={{ color: muted, fontSize: 11, marginTop: 6, textAlign: 'center' }}>
                    <Icon type="lock" style={{ marginRight: 4 }} />
                    {completeActionDisabledReason}
                  </div>
                ) : !actionTakenText.trim() ? (
                  <div style={{ color: muted, fontSize: 11, marginTop: 6, textAlign: 'center' }}>
                    <Icon type="info-circle" style={{ marginRight: 4 }} />
                    Enter what action was taken before completing.
                  </div>
                ) : null}
              </div>
            </>
          )}

          <Divider style={{ marginTop: 24, marginBottom: 16 }}>Attachments</Divider>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Upload Documents or Images</div>
            <Upload
              multiple
              fileList={fileList}
              onChange={handleFileChange}
              onRemove={handleFileRemove}
              onPreview={handlePreview}
              beforeUpload={beforeUpload}
              customRequest={customRequest}
              listType="picture-card"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            >
              {fileList.length < 10 && (
                <div>
                  <Icon type="plus" />
                  <div className="ant-upload-text">Upload</div>
                </div>
              )}
            </Upload>
            <div style={{ fontSize: 12, color: muted, marginTop: 8 }}>
              Supported formats: Images (JPG, PNG, GIF, etc.), PDF, Word, Excel, PowerPoint, Text files. Max file size: 10MB. Max 10 files.
            </div>
          </div>

          {isNewAlert && (
            <div>
              <div style={{ fontSize: 13, color: muted, marginBottom: 6 }}>
                Ignore comment <span style={{ color: '#f5222d' }}>*</span>
              </div>
              <TextArea
                rows={3}
                value={ignoreComment}
                onChange={(e) => setIgnoreComment(e.target.value)}
                placeholder="Add a comment for ignoring this alert (required)"
                style={{ marginBottom: 12 }}
              />
              <Button
                type="default"
                size="large"
                icon="stop"
                disabled={!ignoreComment.trim()}
                onClick={() => {
                  const comment = ignoreComment.trim()
                  if (!comment) {
                    message.error('Please add a comment before ignoring the alert')
                    return
                  }
                  onStatusUpdate && onStatusUpdate(alert, 'IGNORED', 'Ignore', {
                    analysis: analysisText,
                    actionSuggested: actionSuggestedText,
                    actionTaken: actionTakenText,
                    remarks: comment,
                    reason: 'User ignored'
                  })
                }}
                block
                danger
              >
                Ignore Alert
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Assignment History */}
      {Array.isArray(alert.assigneeRecords) && alert.assigneeRecords.filter(r => r.assignTime).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: sectionTitle,
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: `1px solid ${border}`
          }}>
            Assignment History
          </div>
          <div style={{ fontSize: 12 }}>
            {alert.assigneeRecords.filter(r => r.assignTime).map((rec, idx, arr) => (
              <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx < arr.length - 1 ? `1px solid ${border}` : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    Assigned to {rec.userName || 'Unknown'}
                  </span>
                  <span style={{ color: muted, fontSize: 11 }}>
                    {rec.assignTime ? moment(rec.assignTime).format('DD MMM YYYY, HH:mm:ss') : ''}
                  </span>
                </div>
                {rec.assignedByName && (
                  <div style={{ color: bodyText, marginBottom: 4 }}>
                    by <strong>{rec.assignedByName}</strong>
                  </div>
                )}
                {rec.remarks && (
                  <div style={{ color: muted, fontSize: 11 }}>
                    Remarks: {rec.remarks}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status History */}
      {alert.statusHistory && alert.statusHistory.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: sectionTitle,
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: `1px solid ${border}`
          }}>
            Status Change History
          </div>
          <div style={{ fontSize: 12 }}>
            {alert.statusHistory.map((change, idx) => {
              // console.log(change)
              // Get status name - prefer status field, fallback to 'to' field
              const statusName = change.status || change.to || change.currentStatus || 'Unknown'
              // Format status name for display (convert UNDER_ANALYSIS to "Under Analysis")
              const formatStatusName = (status) => {
                if (!status) return 'Unknown'
                return status
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ')
              }
              const displayStatusName = formatStatusName(statusName)

              return (
                <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx < alert.statusHistory.length - 1 ? `1px solid ${border}` : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {change.from && change.to ? `${change.from} → ${change.to}` : displayStatusName}
                      {changesByIndex[idx] && changesByIndex[idx].length > 0 && (
                        <Popover
                          trigger="click"
                          title="Content changed"
                          content={renderContentChanges(changesByIndex[idx])}
                        >
                          <Icon
                            type="info-circle"
                            theme="filled"
                            title="View changed content"
                            style={{ color: accent, cursor: 'pointer', fontSize: 13 }}
                          />
                        </Popover>
                      )}
                    </span>
                    <span style={{ color: muted, fontSize: 11 }}>
                      {change.timestampFormatted || (change.timestamp ? moment(change.timestamp).format('DD MMM YYYY, HH:mm:ss') : (change.date ? moment(change.date).format('DD MMM YYYY, HH:mm:ss') : ''))}
                    </span>
                  </div>
                  <div style={{ color: bodyText, marginBottom: 4 }}>
                    {change.action && (
                      <>
                        Action: <strong>{change.action}</strong>
                      </>
                    )}
                    {change.remarks && !change.action && (
                      <>
                        Remarks: <strong>{change.remarks}</strong>
                      </>
                    )}
                    {(change.userId || change.updatedBy) && (
                      <span style={{ color: muted, marginLeft: 8 }}>by {change.userId || (change.updatedBy && change.updatedBy.name) || 'Unknown'}</span>
                    )}
                  </div>
                  {change.durationInPreviousStatus && (
                    <div style={{
                      background: panelBg,
                      padding: '6px 8px',
                      borderRadius: 4,
                      marginTop: 4,
                      fontSize: 11
                    }}>
                      <Icon type="clock-circle" style={{ marginRight: 4, color: accent }} />
                      <Text strong style={{ color: accent }}>
                        Duration in "{change.from}": {change.durationInPreviousStatus.humanReadable}
                      </Text>
                    </div>
                  )}
                  {change.reason && (
                    <div style={{ color: muted, fontSize: 11, marginTop: 4 }}>
                      Reason: {change.reason}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Lightbox Modal for Documents */}
      <Modal
        visible={lightboxVisible}
        footer={null}
        onCancel={() => setLightboxVisible(false)}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0, textAlign: 'center', background: '#000' }}
      >
        {fileList.length > 0 && (
          <div style={{ position: 'relative', minHeight: '70vh' }}>
            {/* Navigation Buttons */}
            {fileList.length > 1 && (
              <>
                <Button
                  type="primary"
                  shape="circle"
                  icon="left"
                  size="large"
                  style={{
                    position: 'absolute',
                    left: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10
                  }}
                  onClick={() => {
                    const newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : fileList.length - 1
                    setLightboxIndex(newIndex)
                  }}
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon="right"
                  size="large"
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10
                  }}
                  onClick={() => {
                    const newIndex = lightboxIndex < fileList.length - 1 ? lightboxIndex + 1 : 0
                    setLightboxIndex(newIndex)
                  }}
                />
              </>
            )}

            {/* Current Document Display */}
            {fileList[lightboxIndex] && (
              <div style={{ padding: 40 }}>
                {fileList[lightboxIndex].isImage || (fileList[lightboxIndex].mimeType && fileList[lightboxIndex].mimeType.startsWith('image/')) ? (
                  <img
                    src={fileList[lightboxIndex].url}
                    alt={fileList[lightboxIndex].name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div style={{ padding: 60, color: '#fff' }}>
                    <Icon
                      type={getFileIcon(fileList[lightboxIndex].mimeType)}
                      style={{ fontSize: 80, marginBottom: 20 }}
                    />
                    <div style={{ fontSize: 18, marginBottom: 20 }}>
                      {fileList[lightboxIndex].name}
                    </div>
                    <Button
                      type="primary"
                      icon="download"
                      size="large"
                      onClick={() => {
                        window.open(fileList[lightboxIndex].url, '_blank')
                      }}
                    >
                      Open/Download
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Thumbnail Navigation */}
            {fileList.length > 1 && (
              <div style={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 10,
                background: 'rgba(0,0,0,0.7)',
                padding: '10px 20px',
                borderRadius: 8,
                maxWidth: '90%',
                overflowX: 'auto'
              }}>
                {fileList.map((file, index) => (
                  <div
                    key={file.uid}
                    onClick={() => setLightboxIndex(index)}
                    style={{
                      width: 60,
                      height: 60,
                      border: index === lightboxIndex ? '3px solid #1890ff' : '2px solid #666',
                      borderRadius: 4,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      background: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {file.isImage || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Icon
                        type={getFileIcon(file.mimeType)}
                        style={{ fontSize: 30, color: '#fff' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Document Info */}
            <div style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '10px 15px',
              borderRadius: 4,
              fontSize: 12
            }}>
              {lightboxIndex + 1} / {fileList.length}
            </div>
          </div>
        )}
      </Modal>
    </Drawer>
  )
}

export default React.memo(AlertDrawer)
